import type { ReactNode } from 'react';
import React, { useLayoutEffect, useRef, useState, Fragment } from 'react';

import { ArrowRight, Building, JapanMap } from '@travel/icons/ui';
import { cx } from '@travel/utils';

import { PAGE_URL } from '../../constants';

import styles from './breadcrumbs.module.scss';

import type { BreadcrumbItem, BreadcrumbLabels, MenuPageCode } from '../../types';
type Props = {
  breadcrumbs: BreadcrumbItem[];
  activeTab: MenuPageCode;
  labels: BreadcrumbLabels;
  nationalLabel: ReactNode;
  hotelName: string;
  isDated: boolean;
  breadcrumbsHotelLabel: ReactNode;
  isPC?: boolean;
};

export default function Breadcrumbs(props: Props) {
  const {
    hotelName,
    breadcrumbs,
    nationalLabel,
    breadcrumbsHotelLabel,
    isDated,
    isPC = true,
  } = props;
  const [isOverflowing, setIsOverflowing] = useState(false);
  const containerRef = useRef<HTMLElement>(null);
  const breadcrumbsRef = useRef<HTMLUListElement>(null);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const arrowRight = isPC ? <ArrowRight size={12} /> : null;

  const displayBreadcrumbs = [
    ...(isPC
      ? [
          {
            pageUrl: PAGE_URL.TRAVEL_TOP,
            staticUrl: PAGE_URL.TRAVEL_TOP,
          },
        ]
      : []),
    ...breadcrumbs,
    {
      pageDescription: hotelName,
      pageDesc: hotelName,
    },
  ];

  useLayoutEffect(() => {
    if (isPC) return;

    const checkOverflow = () => {
      if (containerRef.current && breadcrumbsRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const breadcrumbsWidth = breadcrumbsRef.current.scrollWidth;
        const hasOverflow = breadcrumbsWidth > containerWidth;
        setIsOverflowing(hasOverflow);

        // Scroll to show the last items by default
        if (hasOverflow && containerRef.current) {
          containerRef.current.scrollLeft = containerRef.current.scrollWidth;
        }
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [displayBreadcrumbs.length, isPC]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isPC && isOverflowing && containerRef.current) {
      const touch = e.touches[0];
      setStartX(touch.pageX - containerRef.current.offsetLeft);
      setScrollLeft(containerRef.current.scrollLeft);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!containerRef.current) return;
    const touch = e.touches[0];
    const x = touch.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 1;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  return (
    <nav
      className={cx(styles.breadcrumbsContainer, !isPC && isOverflowing && styles.overflowing)}
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      data-testid="providerCommonHeader-breadcrumbs"
    >
      <ul className={styles.breadcrumbs} ref={breadcrumbsRef}>
        {displayBreadcrumbs.map((breadcrumb, index) => {
          const isActive = index === displayBreadcrumbs.length - 1;
          const isRoot = index === 0;

          return (
            <Fragment key={index}>
              {index > 0 && arrowRight}
              <li className={cx(isActive && styles.active, isRoot && styles.root)}>
                {isActive ? (
                  isPC ? (
                    breadcrumbsHotelLabel
                  ) : (
                    <Building className={styles.hotelIcon} size={24} />
                  )
                ) : (
                  <a href={isDated ? breadcrumb.pageUrl : breadcrumb.staticUrl}>
                    {isRoot ? (
                      !isPC ? (
                        <JapanMap className={styles.nationalBreadcrumbIcon} size={20} />
                      ) : (
                        nationalLabel
                      )
                    ) : (
                      <span>{breadcrumb.pageDesc}</span>
                    )}
                  </a>
                )}
              </li>
            </Fragment>
          );
        })}
      </ul>
    </nav>
  );
}
